/* =============================================================
   PHONE ENTERPRISE — Repair Booking Wizard
   Standalone module | defer loaded | initialises on DOMContentLoaded
   ============================================================= */

(function () {
  'use strict';

  /* ─── UTILITIES ─────────────────────────────────────────────── */

  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(null, args); }, wait);
    };
  }

  // Random nonce used to build a stable Stripe idempotency key server-side.
  // Constant while ONE attempt is in flight (so a double-submit returns the
  // same checkout session), regenerated when the selection changes (so two
  // genuinely-different bookings never collide) AND after a failed attempt
  // (Stripe replays stored results — incl. 500s — for a key for 24h, so a
  // kept nonce would wedge every retry on the first error; see the catch).
  function makeNonce() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      if (window.crypto && window.crypto.getRandomValues) {
        var a = new Uint8Array(16);
        window.crypto.getRandomValues(a);
        return Array.prototype.map.call(a, function (b) {
          return ('0' + b.toString(16)).slice(-2);
        }).join('');
      }
    } catch (e) { /* fall through */ }
    return 'n' + Date.now() + Math.random().toString(16).slice(2);
  }

  /* ─── BRAND MAP ──────────────────────────────────────────────── */
  function buildBrandMap() {
    return {
      Apple: [
        'iPhone X', 'iPhone XR', 'iPhone XS', 'iPhone XS Max',
        'iPhone 11', 'iPhone 11 Pro', 'iPhone 11 Pro Max',
        'iPhone 12 Mini', 'iPhone 12', 'iPhone 12 Pro', 'iPhone 12 Pro Max',
        'iPhone 13 Mini', 'iPhone 13', 'iPhone 13 Pro', 'iPhone 13 Pro Max',
        'iPhone 14', 'iPhone 14 Plus', 'iPhone 14 Pro', 'iPhone 14 Pro Max',
        'iPhone 15', 'iPhone 15 Plus', 'iPhone 15 Pro', 'iPhone 15 Pro Max',
        'iPhone SE (1st gen)', 'iPhone SE (2nd gen)', 'iPhone SE (3rd gen)'
      ],
      Samsung: [
        'Galaxy S8', 'Galaxy S8+', 'Galaxy S9', 'Galaxy S9+',
        'Galaxy S10', 'Galaxy S10+', 'Galaxy S10e',
        'Galaxy S20', 'Galaxy S20+', 'Galaxy S20 Ultra',
        'Galaxy S21', 'Galaxy S21+', 'Galaxy S21 Ultra',
        'Galaxy S22', 'Galaxy S22+', 'Galaxy S22 Ultra',
        'Galaxy S23', 'Galaxy S23+', 'Galaxy S23 Ultra',
        'Galaxy S24', 'Galaxy S24+', 'Galaxy S24 Ultra',
        'Galaxy A12', 'Galaxy A13', 'Galaxy A14',
        'Galaxy A32', 'Galaxy A51', 'Galaxy A52', 'Galaxy A53',
        'Galaxy Note 10', 'Galaxy Note 10+', 'Galaxy Note 20', 'Galaxy Note 20 Ultra'
      ]
    };
  }

  /* ─── REPAIR ICONS ───────────────────────────────────────────── */
  function repairIcon(title) {
    var t = title.toLowerCase();
    if (t.indexOf('screen') !== -1 || t.indexOf('display') !== -1)
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>';
    if (t.indexOf('battery') !== -1)
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="2" y="7" width="18" height="10" rx="2"/><path d="M22 11v2M7 12h10"/></svg>';
    if (t.indexOf('camera') !== -1)
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>';
    if (t.indexOf('charging') !== -1 || t.indexOf('port') !== -1)
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 12H2l9-9v6h8l-9 9v-6z"/></svg>';
    if (t.indexOf('speaker') !== -1 || t.indexOf('audio') !== -1 || t.indexOf('microphone') !== -1)
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/></svg>';
    if (t.indexOf('front glass') !== -1)
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="5" y="2" width="14" height="20" rx="2"/><rect x="7" y="4" width="10" height="13" rx="1"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>';
    if (t.indexOf('back') !== -1 || t.indexOf('glass') !== -1)
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>';
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>';
  }

  /* ─── FORMAT MONEY ───────────────────────────────────────────── */
  var _fmtMoney = null;
  function formatMoney(cents) {
    if (_fmtMoney === null) {
      _fmtMoney = (window.PETheme && window.PETheme.utils && window.PETheme.utils.formatMoney)
        ? window.PETheme.utils.formatMoney
        : false;
    }
    if (_fmtMoney) return _fmtMoney(cents);
    var fmt = (window.themeSettings && window.themeSettings.moneyFormat) ? window.themeSettings.moneyFormat : '\u00a3{{amount}}';
    // Strip errant space between currency symbol and amount (e.g. "£ " → "£")
    fmt = fmt.replace(/([£€$£]) (\{\{)/, '$1$2');
    return fmt
      .replace('{{amount}}', (cents / 100).toFixed(2))
      .replace('{{amount_no_decimals}}', Math.floor(cents / 100))
      .replace(/<[^>]+>/g, '');
  }

  /* ─── FETCH REPAIRS ──────────────────────────────────────────── */
  function fetchRepairs(handle) {
    var controller = null;
    var timeoutId  = null;
    if (typeof AbortController !== 'undefined') {
      controller = new AbortController();
      timeoutId  = setTimeout(function () { controller.abort(); }, 8000);
    }

    return fetch('/collections/' + handle + '/products.json?limit=250', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal:  controller ? controller.signal : undefined
    })
      .then(function (res) {
        if (timeoutId) clearTimeout(timeoutId);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) { return json.products || []; })
      .catch(function (e) {
        if (timeoutId) clearTimeout(timeoutId);
        if (e.name !== 'AbortError') console.warn('[PE Repair] Fetch failed:', e.message);
        return [];
      });
  }

  /* ─── DATA HELPERS ───────────────────────────────────────────── */
  function getModelsForBrand(brand, products, brandMap) {
    var fullList = brandMap[brand] || [];
    if (!products || products.length === 0) return fullList;
    var inCollection = {};
    for (var i = 0; i < products.length; i++) {
      var variants = products[i].variants || [];
      for (var j = 0; j < variants.length; j++) {
        inCollection[variants[j].option1 || variants[j].title] = true;
      }
    }
    var matched = fullList.filter(function (m) { return inCollection[m]; });
    return matched.length > 0 ? matched : fullList;
  }

  function getRepairsForModel(model, products) {
    var repairs = [];
    products.forEach(function (product) {
      var match = null;
      (product.variants || []).forEach(function (v) {
        if ((v.option1 || v.title) === model) match = v;
      });
      if (match) {
        repairs.push({
          productTitle: product.title,
          variantId:    match.id,
          price:        match.price,
          comparePrice: match.compare_at_price,
          description: (function () {
            if (!product.body_html) return '';
            try {
              var tmp = document.createElement('div');
              tmp.innerHTML = product.body_html;
              return (tmp.textContent || tmp.innerText || '').trim().slice(0, 120);
            } catch (e) {
              return product.body_html.replace(/<[^>]+>/g, '').trim().slice(0, 120);
            }
          })()
        });
      }
    });
    return repairs;
  }

  function baseRepairTitle(title) {
    return title.replace(/\s*[—\-]\s*High Quality Copy/i, '').replace(/\s*[—\-]\s*Original/i, '').trim();
  }

  function isOriginal(title) { return /original/i.test(title); }

  function formatRepairTitle(title) {
    return title
      .replace(/\s*[—\-]\s*High Quality Copy/i, ' (High Quality Copy)')
      .replace(/\s*[—\-]\s*Original/i, ' (Original OEM)');
  }

  /* ─── WIZARD INIT ────────────────────────────────────────────── */
  function initRepairBooking(section) {
    var collectionHandle = section.dataset.collection || 'repairs';
    var labelUrl         = section.dataset.labelUrl   || '/pages/repair-shipping';
    var stripeUrl        = (section.dataset.stripeUrl || '').trim();
    var brandMap         = buildBrandMap();
    var products         = [];
    var isBooking        = false;

    // State — multi-select repairs array
    var selectedBrand   = '';
    var selectedModel   = '';
    var selectedRepairs = []; // [{ variantId, repairTitle, price, pricePence }]
    var bookingNonce    = ''; // idempotency nonce; regenerated whenever the selection changes

    // Cache DOM refs
    var panels                  = section.querySelectorAll('[data-panel]');
    var stepIndicators          = section.querySelectorAll('[data-step-indicator]');
    var progressLines           = section.querySelectorAll('[data-line]');
    var brandCards              = section.querySelectorAll('[data-brand]');
    var selectedBrandLbl        = section.querySelector('[data-selected-brand]');
    var modelList               = section.querySelector('[data-model-list]');
    var modelSearch             = section.querySelector('[data-model-search]');
    var repairGrid              = section.querySelector('[data-repair-grid]');
    var summaryModelInline      = section.querySelector('[data-summary-model-inline]');
    var summaryBrand            = section.querySelector('[data-summary-brand]');
    var summaryModel            = section.querySelector('[data-summary-model]');
    var summaryRepairsContainer = section.querySelector('[data-summary-repairs-container]');
    var summaryPrice            = section.querySelector('[data-summary-price]');
    var bookBtn                 = section.querySelector('[data-book-repair]');
    var labelLink               = section.querySelector('[data-label-link]');
    var fallbackPanel           = section.querySelector('[data-panel="fallback"]');
    var selectionFooter         = section.querySelector('[data-selection-footer]');
    var selectionSummary        = section.querySelector('[data-selection-summary]');
    var continueBtn             = section.querySelector('[data-continue-repairs]');

    if (labelLink && labelUrl && labelUrl !== '/pages/repair-shipping') {
      labelLink.href = labelUrl;
    }

    /* ── Panel navigation ── */
    function showPanel(num) {
      var targetPanel = null;
      panels.forEach(function (panel) {
        var isTarget = panel.dataset.panel === String(num);
        panel.hidden = !isTarget;
        panel.toggleAttribute('inert', !isTarget);
        panel.classList.toggle('is-active', isTarget);
        if (isTarget) targetPanel = panel;
      });

      stepIndicators.forEach(function (ind) {
        var n = parseInt(ind.dataset.stepIndicator, 10);
        ind.classList.toggle('is-active',   n === num);
        ind.classList.toggle('is-complete', n < num);
      });

      progressLines.forEach(function (line) {
        line.classList.toggle('is-complete', parseInt(line.dataset.line, 10) < num);
      });

      if (targetPanel) {
        var focusTarget = targetPanel.querySelector('button:not([disabled]), [tabindex="0"], input');
        if (focusTarget) setTimeout(function () { focusTarget.focus({ preventScroll: true }); }, 50);
      }

      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function showFallback() {
      panels.forEach(function (p) { p.hidden = true; p.toggleAttribute('inert', true); });
      if (fallbackPanel) { fallbackPanel.hidden = false; fallbackPanel.removeAttribute('inert'); }
    }

    /* ── Back buttons ── */
    section.querySelectorAll('[data-back]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = parseInt(btn.dataset.back, 10);
        // Going back to model step: clear repair selection so footer resets
        if (target === 2) {
          selectedRepairs = [];
          updateSelectionFooter();
        }
        showPanel(target);
      });
    });

    /* ── Step 1: Brand ── */
    brandCards.forEach(function (card) {
      card.addEventListener('click', function () {
        selectedBrand = card.dataset.brand;
        brandCards.forEach(function (c) {
          c.classList.remove('is-active');
          c.setAttribute('aria-selected', 'false');
        });
        card.classList.add('is-active');
        card.setAttribute('aria-selected', 'true');
        if (selectedBrandLbl) selectedBrandLbl.textContent = selectedBrand;
        renderModelList('');
        showPanel(2);
      });
    });

    /* ── Step 2: Model — event delegation (one listener, no per-item leak) ── */
    if (modelList) {
      modelList.addEventListener('click', function (e) {
        var item = e.target.closest('[data-model]');
        if (item) selectModel(item.dataset.model);
      });
      modelList.addEventListener('keydown', function (e) {
        var item = e.target.closest('[data-model]');
        if (item && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          selectModel(item.dataset.model);
        }
      });
    }

    function renderModelList(filter) {
      if (!modelList) return;
      var models   = getModelsForBrand(selectedBrand, products, brandMap);
      var lFilter  = filter.toLowerCase();
      var filtered = filter ? models.filter(function (m) { return m.toLowerCase().indexOf(lFilter) !== -1; }) : models;

      if (filtered.length === 0) {
        modelList.innerHTML = '<li class="pe-repair__model-empty">No models found for &ldquo;' + esc(filter) + '&rdquo;</li>';
        return;
      }

      modelList.innerHTML = filtered.map(function (model) {
        return '<li class="pe-repair__model-item" role="option" tabindex="0" data-model="' + esc(model) + '">' + esc(model) + '</li>';
      }).join('');
    }

    if (modelSearch) {
      modelSearch.addEventListener('input', debounce(function () {
        renderModelList(modelSearch.value.trim());
      }, 200));
    }

    function selectModel(model) {
      selectedModel = model;
      selectedRepairs = []; // Clear selection when a new model is chosen
      if (summaryModelInline) summaryModelInline.textContent = model;
      renderRepairs();
      updateSelectionFooter();
      showPanel(3);
    }

    /* ── Step 3: Repairs (multi-select) ── */
    function renderRepairs() {
      if (!repairGrid) return;

      if (products.length === 0) {
        repairGrid.innerHTML = '<p class="pe-repair__loading">No repairs available online yet. <a href="/pages/contact">Contact us</a> for a quote.</p>';
        return;
      }

      var repairs = getRepairsForModel(selectedModel, products);
      if (repairs.length === 0) {
        repairGrid.innerHTML = '<p class="pe-repair__loading">No repairs listed for ' + esc(selectedModel) + ' yet. <a href="/pages/contact">Contact us</a>.</p>';
        return;
      }

      // Group by base title (merges HQ Copy + Original into one card)
      var groups = {};
      var order  = [];
      repairs.forEach(function (r) {
        var base = baseRepairTitle(r.productTitle);
        if (!groups[base]) { groups[base] = []; order.push(base); }
        isOriginal(r.productTitle) ? groups[base].push(r) : groups[base].unshift(r);
      });

      repairGrid.innerHTML = order.map(function (base) {
        var group      = groups[base];
        var first      = group[0];
        var firstPence = Math.round(parseFloat(first.price) * 100);
        var firstStr   = formatMoney(firstPence);

        if (group.length > 1) {
          var optsHTML = group.map(function (r, i) {
            var pence = Math.round(parseFloat(r.price) * 100);
            var label = isOriginal(r.productTitle) ? 'Original OEM' : 'High Quality Copy';
            return '<button type="button" class="pe-repair__quality-opt' + (i === 0 ? ' is-active' : '') + '"' +
              ' data-variant-id="' + esc(r.variantId) + '"' +
              ' data-repair-title="' + esc(r.productTitle) + '"' +
              ' data-price-str="' + esc(formatMoney(pence)) + '"' +
              ' data-price-pence="' + pence + '"' +
              ' aria-pressed="' + (i === 0 ? 'true' : 'false') + '">' +
              esc(label) + '</button>';
          }).join('');

          return '<div class="pe-repair__repair-card" role="option" aria-pressed="false" tabindex="0"' +
            ' data-variant-id="' + esc(first.variantId) + '"' +
            ' data-repair-title="' + esc(first.productTitle) + '"' +
            ' data-repair-price="' + esc(firstStr) + '"' +
            ' data-repair-price-pence="' + firstPence + '">' +
            '<span class="pe-repair__repair-icon">' + repairIcon(base) + '</span>' +
            '<p class="pe-repair__repair-title">' + esc(base) + '</p>' +
            '<div class="pe-repair__quality-toggle" role="group" aria-label="Screen quality">' + optsHTML + '</div>' +
            '<div class="pe-repair__repair-price-row"><span class="pe-repair__repair-price" data-live-price>' + esc(firstStr) + '</span></div>' +
            (first.description ? '<p class="pe-repair__repair-desc">' + esc(first.description) + '</p>' : '') +
            '</div>';
        }

        var compareStr = first.comparePrice
          ? '<span class="pe-repair__repair-compare">' + esc(formatMoney(Math.round(parseFloat(first.comparePrice) * 100))) + '</span>'
          : '';

        return '<div class="pe-repair__repair-card" role="option" aria-pressed="false" tabindex="0"' +
          ' data-variant-id="' + esc(first.variantId) + '"' +
          ' data-repair-title="' + esc(first.productTitle) + '"' +
          ' data-repair-price="' + esc(firstStr) + '"' +
          ' data-repair-price-pence="' + firstPence + '">' +
          '<span class="pe-repair__repair-icon">' + repairIcon(first.productTitle) + '</span>' +
          '<p class="pe-repair__repair-title">' + esc(base) + '</p>' +
          '<div class="pe-repair__repair-price-row"><span class="pe-repair__repair-price">' + esc(firstStr) + '</span>' + compareStr + '</div>' +
          (first.description ? '<p class="pe-repair__repair-desc">' + esc(first.description) + '</p>' : '') +
          '</div>';
      }).join('');
    }

    // Grid event delegation — attached ONCE (outside renderRepairs to avoid duplicate listeners)
    if (repairGrid) {
      repairGrid.addEventListener('click', handleRepairGridClick);
      repairGrid.addEventListener('keydown', handleRepairGridKeydown);
    }

    function handleRepairGridClick(e) {
      var qualityBtn = e.target.closest('.pe-repair__quality-opt');
      var card       = e.target.closest('.pe-repair__repair-card');

      if (qualityBtn && card) {
        // Quality toggle: update card data but don't change selection state.
        // If card is already selected, update its entry in selectedRepairs.
        var oldVariantId = parseInt(card.dataset.variantId, 10);

        card.querySelectorAll('.pe-repair__quality-opt').forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        qualityBtn.classList.add('is-active');
        qualityBtn.setAttribute('aria-pressed', 'true');

        card.dataset.variantId        = qualityBtn.dataset.variantId;
        card.dataset.repairTitle      = qualityBtn.dataset.repairTitle;
        card.dataset.repairPrice      = qualityBtn.dataset.priceStr;
        card.dataset.repairPricePence = qualityBtn.dataset.pricePence;

        var priceEl = card.querySelector('[data-live-price]');
        if (priceEl) priceEl.textContent = qualityBtn.dataset.priceStr;

        // If card is selected, update its entry in selectedRepairs
        if (card.classList.contains('is-active')) {
          var newVariantId = parseInt(qualityBtn.dataset.variantId, 10);
          for (var i = 0; i < selectedRepairs.length; i++) {
            if (selectedRepairs[i].variantId === oldVariantId) {
              selectedRepairs[i] = {
                variantId:   newVariantId,
                repairTitle: qualityBtn.dataset.repairTitle,
                price:       qualityBtn.dataset.priceStr,
                pricePence:  parseInt(qualityBtn.dataset.pricePence || '0', 10)
              };
              break;
            }
          }
          updateSelectionFooter();
        }

      } else if (card) {
        toggleRepairCard(card);
      }
    }

    function handleRepairGridKeydown(e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var card = e.target.closest('.pe-repair__repair-card');
      if (card && !e.target.closest('.pe-repair__quality-opt')) {
        e.preventDefault();
        toggleRepairCard(card);
      }
    }

    function toggleRepairCard(card) {
      var variantId   = parseInt(card.dataset.variantId, 10);
      var repairTitle = card.dataset.repairTitle;
      var price       = card.dataset.repairPrice;
      var pricePence  = parseInt(card.dataset.repairPricePence || '0', 10);

      // Check if already selected
      var existingIdx = -1;
      for (var i = 0; i < selectedRepairs.length; i++) {
        if (selectedRepairs[i].variantId === variantId) { existingIdx = i; break; }
      }

      if (existingIdx !== -1) {
        // Deselect
        selectedRepairs.splice(existingIdx, 1);
        card.classList.remove('is-active');
        card.setAttribute('aria-pressed', 'false');
      } else {
        // Select
        selectedRepairs.push({ variantId: variantId, repairTitle: repairTitle, price: price, pricePence: pricePence });
        card.classList.add('is-active');
        card.setAttribute('aria-pressed', 'true');
      }

      updateSelectionFooter();
    }

    function updateSelectionFooter() {
      // Any change to the selection invalidates the current idempotency nonce
      // so the next booking attempt is treated as a new payment.
      bookingNonce = '';

      if (!selectionFooter) return;
      var count = selectedRepairs.length;

      if (count === 0) {
        selectionFooter.hidden = true;
        if (continueBtn) continueBtn.disabled = true;
        return;
      }

      var total = selectedRepairs.reduce(function (sum, r) { return sum + r.pricePence; }, 0);
      selectionFooter.hidden = false;
      if (selectionSummary) {
        selectionSummary.textContent = count + (count === 1 ? ' repair' : ' repairs') + ' selected \u00b7 ' + formatMoney(total);
      }
      if (continueBtn) continueBtn.disabled = false;
    }

    // Continue button
    if (continueBtn) {
      continueBtn.addEventListener('click', function () { onRepairsConfirmed(); });
    }

    function onRepairsConfirmed() {
      if (selectedRepairs.length === 0) return;

      // Populate summary card
      if (summaryBrand) summaryBrand.textContent = selectedBrand;
      if (summaryModel) summaryModel.textContent = selectedModel;

      if (summaryRepairsContainer) {
        summaryRepairsContainer.innerHTML = selectedRepairs.map(function (r) {
          return '<div class="pe-repair__summary-row">' +
            '<dt>' + esc(formatRepairTitle(r.repairTitle)) + '</dt>' +
            '<dd>' + esc(r.price) + '</dd>' +
            '</div>';
        }).join('');
      }

      var total = selectedRepairs.reduce(function (sum, r) { return sum + r.pricePence; }, 0);
      if (summaryPrice) summaryPrice.textContent = formatMoney(total);

      showPanel(4);
    }

    /* ── Step 4: Book & Pay ── */
    if (bookBtn) {
      bookBtn.addEventListener('click', function () { bookRepair(); });
    }

    function bookRepair() {
      if (selectedRepairs.length === 0 || isBooking) return;
      isBooking = true;
      bookBtn.disabled = true;

      /* ── Option A: Stripe Checkout via Cloudflare Worker ── */
      if (stripeUrl) {
        bookBtn.textContent = 'Preparing secure payment\u2026';

        // Reuse the nonce while this booking attempt is in flight so a
        // double-submit maps to the same Stripe session; it's cleared on
        // selection change AND after a failed attempt (see the catch below —
        // do NOT make it survive failures, or Stripe's 24h stored-error
        // replay wedges the retry loop).
        if (!bookingNonce) bookingNonce = makeNonce();

        var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        var tId  = ctrl ? setTimeout(function () { ctrl.abort(); }, 10000) : null;

        fetch(stripeUrl, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          signal:  ctrl ? ctrl.signal : undefined,
          body: JSON.stringify({
            customerEmail: ((section.dataset.customerEmail || '').trim() || undefined), // signed-in customer → Stripe pre-fills + locks the email, so the booking lands on their account
            brand:   selectedBrand,
            model:   selectedModel,
            nonce:   bookingNonce,
            repairs: selectedRepairs.map(function (r) {
              return { title: r.repairTitle, pricePence: r.pricePence };
            })
          })
        })
        .then(function (res) {
          if (tId) clearTimeout(tId);
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          if (data.url) {
            window.location.href = data.url;
          } else {
            throw new Error(data.error || 'No checkout URL returned');
          }
        })
        .catch(function (err) {
          if (tId) clearTimeout(tId);
          console.error('[PE Repair] Stripe checkout error:', err.message);
          // Fresh nonce for the next attempt: Stripe stores and replays the
          // first result for an idempotency key — including 500s — for 24h,
          // so retrying a failed attempt with the same key replays the same
          // error until the customer changes selection or reloads. A duplicate
          // Checkout Session is not a duplicate charge (sessions only charge
          // on completion), so losing dedupe on the error path costs nothing.
          bookingNonce = '';
          isBooking = false;
          bookBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18" aria-hidden="true"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg> Payment error - try again';
          bookBtn.disabled = false;
          if (window.PETheme && window.PETheme.toast) {
            window.PETheme.toast.show('Could not start payment. Please try again.', 'error');
          }
        });

        return;
      }

      /* ── Option B: Shopify cart fallback — add all repairs as separate items ── */
      bookBtn.textContent = 'Adding to cart\u2026';

      var items = selectedRepairs.map(function (r) {
        return {
          id:         r.variantId,
          quantity:   1,
          properties: {
            'Device Make':  selectedBrand,
            'Device Model': selectedModel,
            'Repair Type':  r.repairTitle
          }
        };
      });

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ items: items })
      })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart error ' + res.status);
        isBooking = false;
        bookBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="18" height="18" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg> Added to cart!';
        bookBtn.disabled = false;
        document.dispatchEvent(new CustomEvent('pe:cart:added'));
        if (window.PETheme && window.PETheme.cart) window.PETheme.cart.open();
      })
      .catch(function (err) {
        console.error('[PE Repair] Cart add failed:', err);
        isBooking = false;
        bookBtn.textContent = 'Could not add - try again';
        bookBtn.disabled = false;
        if (window.PETheme && window.PETheme.toast) {
          window.PETheme.toast.show('Could not add to cart. Please try again.', 'error');
        }
      });
    }

    /* ── Bootstrap: fetch products ── */
    fetchRepairs(collectionHandle).then(function (fetched) {
      products = fetched;

      var loadingEl = section.querySelector('[data-model-loading]');
      if (loadingEl) loadingEl.remove();

      if (products.length === 0) {
        console.info('[PE Repair] Collection "' + collectionHandle + '" empty — showing fallback.');
        showFallback();
      }
    }).catch(function () {
      showFallback();
    });
  }

  /* ─── BOOT ───────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-section-id][data-collection]').forEach(function (section) {
      if (section.querySelector('[data-brand]')) initRepairBooking(section);
    });
  });

})();
